import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './AuthBanner.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// Check test mode once on module load (before React Router can strip it)
const INITIAL_TEST_MODE = new URLSearchParams(window.location.search).get('testauth') === '1';

const AuthBanner = () => {
  const [authStatus, setAuthStatus] = useState({ status: 'checking', message: 'Checking Schwab connection...' });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [authResult, setAuthResult] = useState(null);

  // Use ref to persist test mode across renders
  const testModeRef = useRef(INITIAL_TEST_MODE);
  const testMode = testModeRef.current;

  // Check Schwab auth status
  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth-status`);
      setAuthStatus(response.data);
    } catch (error) {
      setAuthStatus({ status: 'error', message: 'Failed to check auth status' });
    }
  };

  // Get auth URL for manual flow
  const getAuthUrl = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth-url`);
      if (response.data.auth_url) {
        setAuthUrl(response.data.auth_url);
        setShowAuthModal(true);
        setAuthResult(null);
        setCallbackUrl('');
      }
    } catch (error) {
      console.error('Failed to get auth URL:', error);
    }
  };

  // Submit callback URL to exchange for token
  const submitCallback = async () => {
    if (!callbackUrl.trim()) return;

    setSubmitting(true);
    setAuthResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/auth-callback`, { callbackUrl });
      if (response.data.status === 'success') {
        setAuthResult({ type: 'success', message: response.data.message });
        // Recheck auth status after successful auth
        setTimeout(() => {
          checkAuthStatus();
          setShowAuthModal(false);
        }, 1500);
      } else if (response.data.error) {
        setAuthResult({ type: 'error', message: response.data.error });
      }
    } catch (error) {
      setAuthResult({ type: 'error', message: 'Failed to process callback URL' });
    } finally {
      setSubmitting(false);
    }
  };

  // Check auth status on mount and every 60 seconds
  useEffect(() => {
    checkAuthStatus();
    const interval = setInterval(checkAuthStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Don't render anything if auth is valid (unless in test mode)
  if (authStatus.status === 'valid' && !testMode) {
    return null;
  }

  // In test mode, override status to show expired banner
  const displayStatus = testMode
    ? { status: 'expired', message: 'TEST MODE: Schwab token expired - re-authentication required' }
    : authStatus;

  return (
    <>
      <div className={`global-auth-banner ${displayStatus.status}`}>
        <div className="auth-banner-inner">
          <span className="auth-status-icon">
            {displayStatus.status === 'expired' && '⚠️'}
            {displayStatus.status === 'checking' && '⏳'}
            {displayStatus.status === 'error' && '❌'}
          </span>
          <span className="auth-status-text">{displayStatus.message}</span>
          <div className="auth-banner-actions">
            {displayStatus.status === 'expired' && (
              <button className="auth-action-btn primary" onClick={getAuthUrl}>
                🔑 Re-authenticate Schwab
              </button>
            )}
            <button className="auth-action-btn secondary" onClick={checkAuthStatus}>
              ↻ Check Status
            </button>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal-header">
              <h2>🔐 Schwab Authentication</h2>
              <button className="close-btn" onClick={() => setShowAuthModal(false)}>×</button>
            </div>
            <div className="auth-modal-body">
              <div className="auth-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Click the link below to login to Schwab</h3>
                  <a
                    href={authUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="auth-link"
                  >
                    {authUrl}
                  </a>
                  <button
                    className="copy-btn"
                    onClick={() => navigator.clipboard.writeText(authUrl)}
                  >
                    📋 Copy Link
                  </button>
                </div>
              </div>

              <div className="auth-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>After login, you'll be redirected to a page that can't load</h3>
                  <p className="step-hint">This is expected! Copy the full URL from your browser's address bar.</p>
                </div>
              </div>

              <div className="auth-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Paste the redirect URL here</h3>
                  <input
                    type="text"
                    className="callback-input"
                    placeholder="https://127.0.0.1:8182/?code=..."
                    value={callbackUrl}
                    onChange={(e) => setCallbackUrl(e.target.value)}
                  />
                  <button
                    className="submit-btn"
                    onClick={submitCallback}
                    disabled={!callbackUrl.trim() || submitting}
                  >
                    {submitting ? 'Processing...' : '✓ Complete Authentication'}
                  </button>
                </div>
              </div>

              {authResult && (
                <div className={`auth-result ${authResult.type}`}>
                  {authResult.type === 'success' ? '✅' : '❌'} {authResult.message}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthBanner;
